// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient, TyOrganization, TySubscription } from "@/types";
import { catalogUrl, daysUntil, formatDate, formatMoney, formatNumber, formatStorage, readLangValue, storageTone, usedPercent } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Meter from "@/UI/atoms/Meter";
import StatusChip from "@/UI/atoms/StatusChip";
import ClientCard from "@/UI/cards/ClientCard";
import StatCard from "@/UI/cards/StatCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import FactsGrid, { type TyFact } from "@/UI/components/FactsGrid";
import RecordPanel from "@/UI/components/RecordPanel";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import AddStoragePopup from "@/UI/popups/AddStoragePopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import OrganizationFormPopup from "@/UI/popups/OrganizationFormPopup";
import OwnerFormPopup from "@/UI/popups/OwnerFormPopup";
import SubscriptionFormPopup from "@/UI/popups/SubscriptionFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "details" | "usage" | "subscription" | "people";

/**
 * One developer company: what it is, how much of its plan it is using, and what it has paid.
 *
 * Four tabs, because they are four unrelated questions and one scrolling page would bury whichever
 * one brought you here under the three that did not. The tab lives in the URL and **replaces**, so
 * moving between them never stacks history to press back through.
 *
 * `slug` is the path segment rather than an id: `/organizations/al-rabwah` is a link somebody can
 * read, say out loud and recognise — and it is the same slug its public catalog answers on.
 */
export default function OrganizationScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { slug = "" } = useParams();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();

    const tab = (params.get("tab") as TyTab) || "details";

    const company = useFetch<{ organization: TyOrganization; history: TySubscription[] }>(`/organizations/${slug}`, {}, false);
    const action = useFetch("/organizations", {}, false);
    /** Its own staff, from the people endpoint — one list of people in the product, filtered to this company. */
    const people = useFetch<{ clients: TyClient[] }>("/clients", {}, false);

    /**
     * Deferred and asked for here, not on mount: `useFetch` re-runs on `invoke` rather than when its
     * path changes, so a company renamed to a new slug — this screen navigating to its own new
     * address — would otherwise keep showing the record it was opened with.
     */
    useEffect(() => {
        company.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    useEffect(() => {
        if (!action.succeededAt) return;
        company.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    /** Asked for when the tab is opened rather than on every visit to the record. */
    const organizationId = company.data?.organization?._id;

    useEffect(() => {
        if (tab !== "people" || !organizationId) return;
        people.invoke({ query: { organization: organizationId, limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, organizationId]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        setParams(merged, { replace: true });
    };

    const organization = company.data?.organization;

    if (company.error) return <p className="screen-state screen-state--error">{company.error}</p>;
    // A deferred call has neither data nor `loading` on its first frame, so it reads as loading too.
    if (!organization) return <p className="screen-state">{t.common.loading}</p>;

    const name = readLangValue(organization.name, lang) || organization.slug;
    const { storage, counts, subscription } = organization;
    const percent = usedPercent(storage.usedMb, storage.limitMb);
    const history = (company.data?.history ?? []).filter((row) => row._id !== subscription?._id);

    const accessTone = organization.isBlocked
        ? "bad"
        : organization.access === "full"
          ? "good"
          : organization.access === "readOnly"
            ? "bad"
            : "neutral";
    const accessLabel = organization.isBlocked ? t.organizations.access.blocked : t.organizations.access[organization.access];

    const tabs: TyBarTab[] = [
        { key: "details", label: t.organizations.recordTabs.details },
        { key: "people", label: t.organizations.recordTabs.people },
        { key: "usage", label: t.organizations.recordTabs.usage },
        { key: "subscription", label: t.organizations.recordTabs.subscription },
    ];

    const staff = people.data?.clients ?? [];
    /** The owner is the membership, not a flag on the person: they may own one company and work in three. */
    const owner = staff.find((person) => person.memberships.some((entry) => entry.organization._id === organization._id && entry.role === "owner"));

    /** Only what somebody filled in — a grid of em-dashes is a full page that says nothing. */
    const facts: TyFact[] = [
        { key: "slug", label: t.organizations.fields.slug, value: organization.slug, ltr: true },
        { key: "crNumber", label: t.organizations.fields.crNumber, value: organization.crNumber ?? "", ltr: true },
        { key: "phone", label: t.organizations.fields.phone, value: organization.phone ?? "", ltr: true },
        { key: "email", label: t.organizations.fields.email, value: organization.email ?? "", ltr: true },
        { key: "city", label: t.organizations.fields.city, value: organization.city ?? "" },
        { key: "address", label: t.organizations.fields.address, value: organization.address ?? "" },
        {
            key: "languages",
            label: t.organizations.fields.languages,
            value: organization.languages.map((item) => constants.languageNames[item]).join(" · "),
        },
        { key: "theme", label: t.organizations.fields.theme, value: organization.theme },
        { key: "createdAt", label: t.organizations.fields.createdAt, value: formatDate(organization.createdAt, lang) },
    ].filter((fact) => fact.value);

    const openForm = () =>
        addComp({
            name: "organization-form",
            Component: OrganizationFormPopup,
            place: "end",
            anim: "endSlide",
            details: {
                organization,
                /** The slug is editable, so a rename moves this screen's own address with it. */
                onDone: (saved: TyOrganization) =>
                    saved.slug === slug ? company.invoke() : navigate(`/organizations/${saved.slug}`, { replace: true }),
            },
        });

    /**
     * Billing from inside a company, with the subscriptions section's own forms rather than copies.
     * An admin on the phone to a developer is on this screen, not in the billing queue — so recording
     * their renewal has to be reachable from here, and it is the same request either way.
     */
    const openRenewal = () =>
        addComp({
            name: "subscription-form",
            Component: SubscriptionFormPopup,
            anim: "scale",
            details: {
                organization: { _id: organization._id, name: organization.name, slug: organization.slug },
                onDone: () => company.invoke(),
            },
        });

    /**
     * The one account this app creates. A company is sold, created empty, and cannot be signed into
     * by anybody until it has an owner — and its own team screen is the thing that needs one.
     */
    const openOwnerForm = () =>
        addComp({
            name: "owner-form",
            Component: OwnerFormPopup,
            anim: "scale",
            details: {
                organization: { _id: organization._id, name: organization.name, slug: organization.slug, logo: organization.logo },
                onDone: () => {
                    people.invoke({ query: { organization: organization._id, limit: constants.maxPageSize } });
                    // The staff count on the usage tab is derived server-side, so the record is stale now.
                    company.invoke();
                },
            },
        });

    const openAddStorage = (row: TySubscription) =>
        addComp({
            name: "add-storage",
            Component: AddStoragePopup,
            anim: "scale",
            // The popups name the company from the row, so it carries this one's identity with it.
            details: { subscription: { ...row, organization }, onDone: () => company.invoke() },
        });

    const openActions = (anchor: DOMRect) => {
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: openForm },
            {
                key: "catalog",
                label: t.organizations.openCatalog,
                icon: "link",
                onSelect: () => window.open(catalogUrl(organization.slug), "_blank"),
            },
            /* Storage is the one additive change a paid period takes, so it is offered only on one. */
            ...(subscription && ["active", "grace", "coming"].includes(subscription.status)
                ? [{ key: "storage", label: t.subscriptions.addStorage, icon: "drive" as const, onSelect: () => openAddStorage(subscription) }]
                : []),
            {
                key: "block",
                label: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                icon: "block",
                danger: !organization.isBlocked,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                            message: (organization.isBlocked ? t.organizations.confirmUnblock : t.organizations.confirmBlock).replace("{name}", name),
                            confirmLabel: organization.isBlocked ? t.organizations.unblock : t.organizations.block,
                            danger: !organization.isBlocked,
                            onConfirm: () =>
                                action.invoke({
                                    method: "PATCH",
                                    path: `/organizations/${organization._id}`,
                                    data: { isBlocked: !organization.isBlocked },
                                }),
                        },
                    }),
            },
        ];

        addComp({
            name: "organization-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /** One period, read rather than edited: a renewal is a new row, so there is nothing here to change. */
    const renderPeriod = (row: TySubscription, isCurrent: boolean) => {
        const left = daysUntil(row.endsAt);
        /**
         * Granted packs only. A pack the company has asked for and not transferred for yet grants
         * nothing, so counting it here would tell an admin this company has space it does not.
         */
        const addonGb = row.addons
            .filter((addon) => addon.status !== "pending" && addon.status !== "cancelled")
            .reduce((total, addon) => total + addon.storageGb, 0);
        const waiting = row.addons.filter((addon) => addon.status === "pending");

        return (
            <section key={row._id} className={`organization-screen__period${isCurrent ? " organization-screen__period--current" : ""}`}>
                <header className="organization-screen__period-head">
                    <span className="organization-screen__period-title">
                        {isCurrent ? `${t.subscriptions.currentPeriod} · ` : ""}
                        {t.subscriptions.periods[row.period]}
                    </span>
                    <StatusChip
                        size="sm"
                        tone={row.status === "active" ? "good" : row.status === "grace" ? "warn" : "neutral"}
                        label={t.subscriptions.statuses[row.status]}
                    />
                    <span className="organization-screen__period-amount">
                        {formatMoney(row.price)} {t.subscriptions.currency}
                    </span>
                </header>

                <FactsGrid
                    facts={[
                        { key: "basePrice", label: t.subscriptions.fields.basePrice, value: formatMoney(row.basePrice), ltr: true },
                        {
                            key: "addons",
                            label: t.subscriptions.fields.addons,
                            value: addonGb ? `${addonGb} GB` : "",
                            ltr: true,
                        },
                        {
                            key: "addonsWaiting",
                            label: t.subscriptions.addonWaiting.replace("{count}", String(waiting.length)),
                            value: waiting.length ? `${waiting.reduce((total, addon) => total + addon.storageGb, 0)} GB` : "",
                            ltr: true,
                        },
                        { key: "startsAt", label: t.subscriptions.fields.startsAt, value: formatDate(row.startsAt, lang) },
                        { key: "endsAt", label: t.subscriptions.fields.endsAt, value: formatDate(row.endsAt, lang) },
                        { key: "paidAt", label: t.subscriptions.fields.paidAt, value: formatDate(row.paidAt, lang) },
                        { key: "note", label: t.subscriptions.fields.note, value: row.note ?? "" },
                    ].filter((fact) => fact.value)}
                />

                {/* The date that matters, said in days rather than left as a date to subtract from today. */}
                {isCurrent && left !== null && (
                    <p className="organization-screen__period-left">
                        {left >= 0 ? t.organizations.endsIn.replace("{days}", String(left)) : t.organizations.lapsed.replace("{days}", String(-left))}
                    </p>
                )}
            </section>
        );
    };

    return (
        <section className="organization-screen">
            <RecordPanel
                avatar={<Avatar name={name} picture={organization.logo} size={46} rounded bordered />}
                title={name}
                subtitle={organization.slug}
                chips={
                    <>
                        <StatusChip size="sm" tone={accessTone} label={accessLabel} />
                        {subscription?.status === "grace" && <StatusChip size="sm" tone="warn" label={t.organizations.access.grace} />}
                        {subscription && <StatusChip size="sm" tone="dark" label={t.subscriptions.periods[subscription.period]} />}
                        {organization.city && <StatusChip size="sm" tone="neutral" label={organization.city} />}
                    </>
                }
                primary={{ label: t.organizations.openCatalog, onPress: () => window.open(catalogUrl(organization.slug), "_blank") }}
                onMore={openActions}
            >
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "details" ? null : key })}
                    /**
                     * The bar's action belongs to the tab that is open. The subscription tab records a
                     * renewal — a new row queued behind the live period, never an edit of it, so it is
                     * an add in the plainest sense — and the team tab seats the owner, but only while
                     * that seat is empty and only once the list has come back to prove it is.
                     */
                    addLabel={
                        tab === "people" ? t.organizations.setOwner : subscription ? t.subscriptions.renewTitle : t.subscriptions.recordSubscription
                    }
                    onAdd={tab === "subscription" ? openRenewal : tab === "people" && !!people.data && !owner ? openOwnerForm : undefined}
                />
            </RecordPanel>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}

            {tab === "details" && <FactsGrid facts={facts} />}

            {/* --- Who works here. The owner is the only one this side can seat ---------------- */}
            {tab === "people" && (
                <>
                    {people.loading && <p className="screen-state">{t.common.loading}</p>}
                    {people.error && <p className="screen-state screen-state--error">{people.error}</p>}
                    {!people.loading && !people.error && !staff.length && <p className="screen-state">{t.organizations.noPeople}</p>}

                    <div className="list-screen__rows">
                        {staff.map((person) => (
                            <ClientCard key={person._id} client={person} onOpen={() => navigate(`/clients/${person._id}`)} />
                        ))}
                    </div>

                    {/* Said once, here, rather than discovered by looking for an add button. */}
                    {!!owner && <p className="organization-screen__quota">{t.organizations.peopleHint}</p>}
                </>
            )}

            {/* --- Usage: what it has put in, and the one number that can stop it working ------- */}
            {tab === "usage" && (
                <>
                    <div className="stat-row">
                        <StatCard icon="properties" value={formatNumber(counts.projects)} label={t.organizations.counts.projects} />
                        <StatCard icon="building" value={formatNumber(counts.units)} label={t.organizations.counts.units} />
                        <StatCard icon="team" value={formatNumber(counts.customers)} label={t.organizations.counts.customers} />
                        <StatCard icon="crown" value={formatNumber(counts.clients)} label={t.organizations.counts.clients} />
                    </div>

                    <section className="organization-screen__panel">
                        <Meter
                            label={t.organizations.storage}
                            value={t.organizations.storageOf
                                .replace("{used}", formatStorage(storage.usedMb, lang))
                                .replace("{limit}", formatStorage(storage.limitMb, lang))}
                            percent={percent}
                            tone={storageTone(percent)}
                        />

                        {/* A company with no subscription is not broken — it is inside the free quota. */}
                        {organization.access === "free" && (
                            <p className="organization-screen__quota">
                                {t.organizations.freeQuota
                                    .replace("{projects}", String(constants.freeLimits.projects))
                                    .replace("{units}", String(constants.freeLimits.units))
                                    .replace("{storage}", formatStorage(constants.freeLimits.storageMb, lang))}
                            </p>
                        )}
                    </section>
                </>
            )}

            {/* --- Subscription: the live period, then every one before it ---------------------- */}
            {tab === "subscription" && (
                <>
                    {!subscription && <p className="screen-state">{t.organizations.noSubscription}</p>}
                    {subscription && renderPeriod(subscription, true)}

                    {history.length > 0 && (
                        <>
                            <h2 className="organization-screen__section-title">{t.organizations.history}</h2>
                            {history.map((row) => renderPeriod(row, false))}
                        </>
                    )}
                </>
            )}
        </section>
    );
}
